"use client";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { useTranslations } from "next-intl";
import React from "react";

// ============================================================================
// Demo data — held local to the section
// ============================================================================

type AgentRow = {
  key: 1 | 2 | 3 | 4 | 5;
  initials: string;
  calls: number;
  connectPct: number;
  gatekeeperPct: number;
  objectionPct: number;
  outcomeInterestedPct: number;
  outcomeDemoPct: number;
  // (rest is "no interest" → derived)
  tagPct: [number, number, number]; // for the 3 custom tags
};

const AGENTS: AgentRow[] = [
  {
    key: 1,
    initials: "AS",
    calls: 142,
    connectPct: 38,
    gatekeeperPct: 72,
    objectionPct: 65,
    outcomeInterestedPct: 28,
    outcomeDemoPct: 12,
    tagPct: [73, 18, 42],
  },
  {
    key: 2,
    initials: "TB",
    calls: 128,
    connectPct: 31,
    gatekeeperPct: 54,
    objectionPct: 48,
    outcomeInterestedPct: 22,
    outcomeDemoPct: 8,
    tagPct: [51, 12, 28],
  },
  {
    key: 3,
    initials: "LR",
    calls: 155,
    connectPct: 42,
    gatekeeperPct: 78,
    objectionPct: 71,
    outcomeInterestedPct: 35,
    outcomeDemoPct: 15,
    tagPct: [81, 22, 55],
  },
  {
    key: 4,
    initials: "MW",
    calls: 97,
    connectPct: 24,
    gatekeeperPct: 38,
    objectionPct: 30,
    outcomeInterestedPct: 15,
    outcomeDemoPct: 5,
    tagPct: [32, 8, 15],
  },
  {
    key: 5,
    initials: "HV",
    calls: 134,
    connectPct: 35,
    gatekeeperPct: 62,
    objectionPct: 55,
    outcomeInterestedPct: 25,
    outcomeDemoPct: 10,
    tagPct: [65, 15, 38],
  },
];

// ============================================================================
// Root Section
// ============================================================================

export const DashboardsSection = () => {
  return (
    <section className="dark relative w-full overflow-hidden bg-neutral-950">
      <SectionBackground />
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-20 md:py-28 lg:py-36">
        <BrandReveal />
        <div className="mt-12 lg:mt-20">
          <DashboardScene />
        </div>
        <div className="mt-14 lg:mt-24">
          <CapabilityGrid />
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// Background — dark band with hairlines
// ============================================================================

const SectionBackground = () => (
  <>
    <div className="absolute inset-x-0 top-0 h-px bg-neutral-800" />
    <div className="absolute inset-x-0 bottom-0 h-px bg-neutral-800" />
  </>
);

// ============================================================================
// Block 1 — Brand Reveal
// ============================================================================

const BrandReveal = () => {
  const t = useTranslations("dashboards");
  return (
    <div className="flex flex-col items-center text-center">
      <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-500">
        <ChartIcon className="h-3.5 w-3.5" />
        Dashboards
      </p>

      <h2 className="mt-4 text-3xl md:text-5xl font-bold leading-tight tracking-tight text-white max-w-3xl">
        {t("tagline")}
      </h2>

      <p className="mt-5 text-sm md:text-base text-neutral-400 max-w-2xl leading-relaxed">
        {t("lead")}
      </p>
    </div>
  );
};

// ============================================================================
// Block 2 — Dashboard Scene
// ============================================================================

const DashboardScene = () => {
  const t = useTranslations("dashboards");
  return (
    <div className="relative rounded-2xl border border-neutral-800 bg-neutral-950 overflow-hidden">

      {/* Toolbar */}
      <div className="border-b border-neutral-800 bg-neutral-900/40 px-5 py-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-white truncate">
            {t("panel_title")}
          </div>
          <div className="text-[11px] text-neutral-500 mt-0.5">
            {t("panel_meta")}
          </div>
        </div>
        <TabStrip />
      </div>

      {/* Body */}
      <div className="grid grid-cols-1 lg:grid-cols-12">
        <AgentTablePanel />
        <HeatmapPanel />
      </div>

      {/* Footer */}
      <div className="border-t border-neutral-800 bg-neutral-900/40 px-5 py-3 flex items-center gap-2">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        <span className="text-[10px] text-neutral-500">{t("footer_meta")}</span>
      </div>
    </div>
  );
};

const TabStrip = () => {
  const t = useTranslations("dashboards");
  const tabs = [t("tab_1"), t("tab_2"), t("tab_3"), t("tab_4")];
  return (
    <div className="flex items-center gap-1 flex-shrink-0 overflow-x-auto">
      {tabs.map((label, i) => (
        <span
          key={i}
          className={cn(
            "text-[11px] font-medium px-2.5 py-1 rounded-md whitespace-nowrap",
            i === 0
              ? "bg-red-500/15 text-red-300 ring-1 ring-red-500/30"
              : "text-neutral-500 hover:text-neutral-300"
          )}
        >
          {label}
        </span>
      ))}
    </div>
  );
};

// ----------------------------------------------------------------------------
// Agent table (left)
// ----------------------------------------------------------------------------

const AgentTablePanel = () => {
  const t = useTranslations("dashboards");
  return (
    <div className="lg:col-span-8 border-b lg:border-b-0 lg:border-r border-neutral-800 p-5">
      {/* Header row */}
      <div className="grid grid-cols-12 gap-3 text-[10px] uppercase tracking-wider text-neutral-500 font-semibold pb-2 border-b border-neutral-800">
        <div className="col-span-3">{t("col_agent")}</div>
        <div className="col-span-1 text-right">{t("col_calls")}</div>
        <div className="col-span-1 text-right">{t("col_connect")}</div>
        <div className="col-span-3">{t("col_gatekeeper")}</div>
        <div className="col-span-2">{t("col_objection")}</div>
        <div className="col-span-2">{t("col_outcome")}</div>
      </div>

      {/* Rows */}
      <ul className="mt-1">
        {AGENTS.map((agent, i) => (
          <AgentTableRow key={agent.key} agent={agent} index={i} />
        ))}
      </ul>

      {/* Outcome legend */}
      <div className="mt-4 pt-3 border-t border-neutral-800 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[10px] text-neutral-500">
        <LegendDot
          color="bg-emerald-500"
          label={t("outcome_legend_interested")}
        />
        <LegendDot color="bg-blue-500" label={t("outcome_legend_demo")} />
        <LegendDot color="bg-neutral-600" label={t("outcome_legend_no")} />
      </div>
    </div>
  );
};

const AgentTableRow = ({
  agent,
  index,
}: {
  agent: AgentRow;
  index: number;
}) => {
  const t = useTranslations("dashboards");
  const noPct =
    100 - agent.outcomeInterestedPct - agent.outcomeDemoPct;

  return (
    <motion.li
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.15 + index * 0.08, ease: "easeOut" }}
      className="grid grid-cols-12 gap-3 items-center py-3 border-b border-neutral-800/60 last:border-b-0 text-sm"
    >
      {/* Name */}
      <div className="col-span-3 flex items-center gap-2.5 min-w-0">
        <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-neutral-800 text-[10px] font-semibold text-neutral-300 ring-1 ring-neutral-700">
          {agent.initials}
        </span>
        <span className="text-neutral-200 font-medium truncate">
          {t(`agent_${agent.key}_name`)}
        </span>
      </div>

      {/* Calls */}
      <div className="col-span-1 text-right text-neutral-300 tabular-nums">
        {agent.calls}
      </div>

      {/* Connect */}
      <div className="col-span-1 text-right text-neutral-300 tabular-nums">
        {agent.connectPct}%
      </div>

      {/* Gatekeeper Pass */}
      <div className="col-span-3">
        <ProgressBar value={agent.gatekeeperPct} index={index} />
      </div>

      {/* Objection Handling */}
      <div className="col-span-2">
        <ProgressBar value={agent.objectionPct} index={index} accent="amber" />
      </div>

      {/* Outcome stacked bar */}
      <div className="col-span-2">
        <StackedBar
          interested={agent.outcomeInterestedPct}
          demo={agent.outcomeDemoPct}
          no={noPct}
          index={index}
        />
      </div>
    </motion.li>
  );
};

const ProgressBar = ({
  value,
  index,
  accent = "red",
}: {
  value: number;
  index: number;
  accent?: "red" | "amber";
}) => {
  const fill =
    accent === "red"
      ? "bg-red-500"
      : "bg-gradient-to-r from-amber-500 via-amber-400 to-amber-500";
  return (
    <div className="flex items-center gap-2">
      <div className="relative flex-1 h-1.5 rounded-full bg-neutral-800 overflow-hidden">
        <motion.div
          className={cn("absolute inset-y-0 left-0 rounded-full", fill)}
          initial={{ width: 0 }}
          animate={{ width: `${value}%` }}
          transition={{
            duration: 0.9,
            delay: 0.4 + index * 0.08,
            ease: "easeOut",
          }}
        />
      </div>
      <span className="text-[11px] font-mono tabular-nums text-neutral-400 w-9 text-right">
        {value}%
      </span>
    </div>
  );
};

const StackedBar = ({
  interested,
  demo,
  no,
  index,
}: {
  interested: number;
  demo: number;
  no: number;
  index: number;
}) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{
        duration: 0.5,
        delay: 0.45 + index * 0.08,
        ease: "easeOut",
      }}
      className="flex h-2 rounded-full overflow-hidden bg-neutral-800"
    >
      <span
        className="bg-emerald-500"
        style={{ width: `${interested}%` }}
        title={`Interessiert ${interested}%`}
      />
      <span
        className="bg-blue-500"
        style={{ width: `${demo}%` }}
        title={`Demo ${demo}%`}
      />
      <span
        className="bg-neutral-600"
        style={{ width: `${no}%` }}
        title={`Kein Interesse ${no}%`}
      />
    </motion.div>
  );
};

const LegendDot = ({ color, label }: { color: string; label: string }) => (
  <span className="inline-flex items-center gap-1.5">
    <span className={cn("h-2 w-2 rounded-full", color)} />
    <span>{label}</span>
  </span>
);

// ----------------------------------------------------------------------------
// Heatmap (right)
// ----------------------------------------------------------------------------

const HeatmapPanel = () => {
  const t = useTranslations("dashboards");
  const tagLabels = [t("tag_1"), t("tag_2"), t("tag_3")];

  return (
    <div className="lg:col-span-4 p-5 bg-neutral-950">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="flex items-center gap-1.5">
            <FlameIcon className="h-3.5 w-3.5 text-red-500" />
            <span className="text-[10px] font-semibold tracking-wider text-red-400 uppercase">
              {t("tags_panel_title")}
            </span>
          </div>
          <div className="text-[10px] text-neutral-500 mt-0.5">
            {t("tags_panel_meta")}
          </div>
        </div>
      </div>

      {/* Tag column headers */}
      <div className="grid grid-cols-[1fr_repeat(3,minmax(0,1fr))] gap-2 items-end mb-2">
        <div />
        {tagLabels.map((label, i) => (
          <div
            key={i}
            className="text-[9px] uppercase tracking-wider text-neutral-500 font-medium leading-tight text-center"
          >
            {label}
          </div>
        ))}
      </div>

      {/* Rows */}
      <ul className="space-y-1.5">
        {AGENTS.map((agent, rowIdx) => (
          <li
            key={agent.key}
            className="grid grid-cols-[1fr_repeat(3,minmax(0,1fr))] gap-2 items-center"
          >
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-neutral-800 text-[9px] font-semibold text-neutral-400 ring-1 ring-neutral-700">
                {agent.initials}
              </span>
              <span className="text-[11px] text-neutral-400 truncate">
                {t(`agent_${agent.key}_name`).split(" ")[0]}
              </span>
            </div>
            {agent.tagPct.map((pct, colIdx) => (
              <HeatmapCell
                key={colIdx}
                value={pct}
                rowIndex={rowIdx}
                colIndex={colIdx}
              />
            ))}
          </li>
        ))}
      </ul>
    </div>
  );
};

const HeatmapCell = ({
  value,
  rowIndex,
  colIndex,
}: {
  value: number;
  rowIndex: number;
  colIndex: number;
}) => {
  // Map 0–100 → 0.08–0.85 alpha for red
  const alpha = 0.08 + (value / 100) * 0.77;
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{
        duration: 0.4,
        delay: 0.5 + rowIndex * 0.06 + colIndex * 0.04,
        ease: "easeOut",
      }}
      className="relative h-7 rounded border border-red-500/15 flex items-center justify-center"
      style={{ backgroundColor: `rgba(239,68,68,${alpha})` }}
    >
      <span
        className={cn(
          "text-[10px] font-mono tabular-nums",
          value > 50 ? "text-white" : "text-neutral-300"
        )}
      >
        {value}%
      </span>
    </motion.div>
  );
};

// ============================================================================
// Block 3 — Capability Grid
// ============================================================================

type TileConfig = { key: string; icon: React.ReactNode };

const TILES: TileConfig[] = [
  { key: "t1", icon: <IconCustomChart /> },
  { key: "t2", icon: <IconFilter /> },
  { key: "t3", icon: <IconUserDrill /> },
  { key: "t4", icon: <IconLive /> },
];

const CapabilityGrid = () => {
  const t = useTranslations("dashboards");
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {TILES.map((tile) => (
        <CapabilityTile
          key={tile.key}
          icon={tile.icon}
          label={t(`${tile.key}_label`)}
          desc={t(`${tile.key}_desc`)}
        />
      ))}
    </div>
  );
};

const CapabilityTile = ({
  icon,
  label,
  desc,
}: {
  icon: React.ReactNode;
  label: string;
  desc: string;
}) => (
  <div className="relative rounded-xl border border-neutral-800 bg-neutral-900/50 p-5">
    <div className="relative">
      <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-neutral-800 text-red-500">
        {icon}
      </div>
      <h3 className="mt-3 text-sm md:text-base font-semibold text-white">
        {label}
      </h3>
      <p className="mt-1 text-xs md:text-sm text-neutral-400 leading-relaxed">
        {desc}
      </p>
    </div>
  </div>
);

// ============================================================================
// Icons
// ============================================================================

function FlameIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <path d="M12 2.5c1.3 3.1 3.8 4.6 4.7 6.9.9 2.3.3 4.7-1.4 6.4-.6.6-1.3 1-2.1 1.2.5-.9.6-2 .2-3-.5-1.2-1.7-1.9-2.9-1.9-.3 1-.9 1.9-1.7 2.5-1.7 1.3-2.3 3.5-1.6 5.4.5 1.4 1.6 2.6 3 3-3.7-.2-7-3.1-7.4-6.9-.4-4.1 2.4-7.7 5.5-10.1C10.3 4.7 11.4 3.6 12 2.5z" />
    </svg>
  );
}

function ChartIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M4 20V8M10 20V4M16 20v-9M22 20H2" />
    </svg>
  );
}

function IconCustomChart() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function IconFilter() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <path d="M3 5h18l-7 9v6l-4-2v-4z" />
    </svg>
  );
}

function IconUserDrill() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <circle cx="12" cy="9" r="3.5" />
      <path d="M5 20c1-3.5 4-5.5 7-5.5s6 2 7 5.5" />
      <path d="M16 12l3 3 3-3" />
    </svg>
  );
}

function IconLive() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M5 12a7 7 0 0 1 14 0M2 12a10 10 0 0 1 20 0" />
    </svg>
  );
}
